import { Euler, Quaternion } from "three";
class MathUtils {
    /**
     * Clamp the value within a defined range.
     * @param min the lower end of the range.
     * @param value the default value.
     * @param max the higher end of the range.
     * @returns the clamped value.
     */
    static clamp(min, value, max) {
        return Math.min(Math.max(value, min), max);
    }
    /**
     * Return a random number within the defined range.
     * @param min The lower end of the range, inclusive.
     * @param max The higher end of the range, exclusive.
     * @returns A random number within the defined range.
     */
    static randRange(min, max) {
        return Math.random() * (max - min) + min;
    }
    /**
     * Cutoff decimal points of a number after a defined threshold.
     * @param value the value to be modified
     * @param decimalPoint the decimal point at which the value should be cut. Defaults to 10.
     * @param method the math function that should be used (either: `ceil`, `floor`, `round` or `trunc`). Defaults to "round".
     * @returns the resulting number.
     */
    static cutDecimals(value, decimalPoint = 10, method = "round") {
        return Math[method](value * decimalPoint) / decimalPoint;
    }
    /**
     * Return a quaternion with only the rotation along the specified axis preserved.
     * @param rotation The quaternion to get the axis of.
     * @param axis A 3D vector representing the axis that should be preserved on the quaternion. Only one value should be set, the rest should be 0.
     * @param coordinate Either "x", "y" or "z". Should match the above axis.
     * @param order The EulerOrder to use when retrieving the euler angle.
     * @returns A copy of the rotation quaternion that only preserves the specified axis, the others are set to 0.
     */
    static getRotationAlongAxis(rotation, axis, coordinate, order) {
        const eulerRotation = new Euler().setFromQuaternion(rotation, order);
        return new Quaternion().setFromAxisAngle(axis, eulerRotation[coordinate]);
    }
    /**
     * Return a quaternion with only the rotation along the specified axis preserved.
     * @param rotation The quaternion to get the axis of.
     * @param axis A 3D vector representing the axis that should be preserved on the quaternion. Only one value should be set, the rest should be 0.
     * @param coordinate Either "x", "y" or "z". Should match the above axis.
     * @param order The EulerOrder to use when retrieving the euler angle.
     * @returns A copy of the rotation quaternion that only preserves the specified axis, the others are set to 0.
     */
    static rotateAlongAxis(angle, axis) {
        return new Quaternion().setFromAxisAngle(axis, angle);
    }
}
export { MathUtils };
